import { Controller } from "@hotwired/stimulus"

// Lets a form be submitted once, and once only.
//
// The checkout forms opt out of Turbo on purpose (PaymentsController#create
// answers with a redirect to the merchant's own domain, which Turbo cannot
// follow), so the browser submits them the old way and Turbo never gets to
// disable the submitter for the duration of the request. `data-disable-with`,
// which `submit_tag` still emits by default, is no help either: it is
// implemented by @rails/ujs and this app does not ship it. Without either, a
// second click while the merchant is being called is a second real payment.
//
// The guard is the flag, not the disabled attribute: it is raised synchronously
// inside the submit event, so every later submit — a second click, Enter in a
// field, anything — is swallowed. Disabling the buttons and swapping their
// label is only what the customer sees, and it happens on the next tick so the
// submission already on its way is never touched (a submitter disabled from
// inside its own submit event drops out of the form data set).
const LOADING_CLASS = "is-loading"

export default class extends Controller {
  static values = { lockedText: { type: String, default: "Отправляем…" } }

  connect() {
    this.locked = false
    this.unlockHandler = () => this.unlock()

    // A locked form can come back on screen without ever being rebuilt, and
    // then its only button is dead for good. Turbo restores its snapshot of
    // this page on Back — disabled attribute, swapped label and all, since it
    // caches the DOM as it stood — and that snapshot is already on screen by
    // the time this controller connects, so undo it right here.
    this.unlock()
    // The browser's own back/forward cache restores the page the same way but
    // without reconnecting anything; `pageshow` is the only announcement.
    window.addEventListener("pageshow", this.unlockHandler)
    // And a Turbo-driven form whose request ends without a navigation — a
    // validation error rendered in place, a dropped connection — never
    // reconnects either, so nothing else would ever let go of the lock.
    this.element.addEventListener("turbo:submit-end", this.unlockHandler)
  }

  disconnect() {
    window.removeEventListener("pageshow", this.unlockHandler)
    this.element.removeEventListener("turbo:submit-end", this.unlockHandler)
  }

  lock(event) {
    if (this.locked) {
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }

    this.locked = true
    setTimeout(() => this.showLoading(), 0)
  }

  showLoading() {
    // The lock may already have been let go while this was waiting its turn (a
    // Turbo submission that came straight back), and then there is nothing to
    // show — painting it now would disable the button for good.
    if (!this.locked) return

    this.element.setAttribute("aria-busy", "true")

    this.submitters().forEach((element) => {
      element.dataset.submitGuardLabel = this.labelOf(element)
      this.relabel(element, this.lockedTextValue)
      element.classList.add(LOADING_CLASS)
      element.disabled = true
    })
  }

  unlock() {
    this.locked = false
    this.element.removeAttribute("aria-busy")

    // Only what this guard itself took out: the saved label is the marker, so a
    // button the page deliberately rendered disabled is never switched on.
    this.submitters().forEach((element) => {
      const label = element.dataset.submitGuardLabel
      if (label === undefined) return

      this.relabel(element, label)
      delete element.dataset.submitGuardLabel
      element.classList.remove(LOADING_CLASS)
      element.disabled = false
    })
  }

  // Everything that can submit this form. `button` without a type submits too —
  // the modal's own "Отмена" and close controls say type="button" and stay out.
  submitters() {
    return Array.from(
      this.element.querySelectorAll("input[type=submit], button[type=submit], button:not([type])")
    )
  }

  labelOf(element) {
    return element.tagName === "INPUT" ? element.value : element.textContent
  }

  relabel(element, text) {
    if (element.tagName === "INPUT") {
      element.value = text
    } else {
      element.textContent = text
    }
  }
}
